import { Button, Heading, SkeletonText, StackProps, VStack } from "@chakra-ui/react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { useEffect, useMemo } from "react";
import { FloatingField } from "design-system";
import { NewHireJourney } from "models/v2/newHireJourney";

const ONBOARDING_PLAN_URL_FIELD = {
  label: "Onboarding Plan Url",
  name: "onboardingPlanUrl",
  helperText: "Url should include the prefix (http:// or https://)",
  type: "url",
  defaultValue: "https://",
};

const resourceSchema = yup.object({
  onboardingPlanUrl: yup.string().url().nullable().default(null).label(ONBOARDING_PLAN_URL_FIELD.label),
});

const values = (data: NewHireJourney | undefined) => ({
  onboardingPlanUrl: data?.onboardingPlanUrl || null,
});

interface NewHireJourneyFormValues {
  onboardingPlanUrl: string | null;
}

interface NewHireJourneyFormProps extends StackProps {
  data: NewHireJourney | undefined;
  isLoading: boolean;
  onFormSubmit: (data: NewHireJourneyFormValues) => void;
}

export default function NewHireJourneyForm({ data, isLoading, onFormSubmit, ...rest }: NewHireJourneyFormProps) {
  const defaultValues = useMemo(() => values(data), [data]);

  const {
    control,
    formState: { errors, isDirty, isValid },
    handleSubmit,
    reset,
  } = useForm({
    mode: "onBlur",
    resolver: yupResolver(resourceSchema),
    defaultValues,
  });

  useEffect(() => {
    reset({ ...defaultValues });
  }, [defaultValues, reset]);

  if (!data) return <SkeletonText mt="4" noOfLines={12} spacing="4" skeletonHeight="4" speed={1.75} />;

  return (
    <VStack spacing="3" alignItems="start" width="100%" {...rest}>
      <Heading my="4" size="xs" color="fg.muted">
        Update Onboarding Plan
      </Heading>
      <VStack spacing="3" width="100%">
        <FloatingField name={ONBOARDING_PLAN_URL_FIELD.name} errors={errors.onboardingPlanUrl?.message}>
          <FloatingField.Label>{ONBOARDING_PLAN_URL_FIELD.label}</FloatingField.Label>
          <FloatingField.Text
            control={control}
            name={ONBOARDING_PLAN_URL_FIELD.name}
            type={ONBOARDING_PLAN_URL_FIELD.type}
          />
        </FloatingField>
      </VStack>
      <Button
        isLoading={isLoading}
        isDisabled={!(isDirty && isValid)}
        alignSelf="end"
        width="30%"
        minW={40}
        onClick={handleSubmit(onFormSubmit)}
      >
        Update
      </Button>
    </VStack>
  );
}
